import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import { FormProvider, useFieldArray, useWatch } from "react-hook-form";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { array, InferType, number, object, string } from "yup";
import {
    getKeyValue,
    TableBody,
    TableColumn,
    TableRow,
    Spinner,
    TableHeader,
    TableCell,
    Table,
    Input,
    Chip,
    Button,
} from "@heroui/react";
import AutoCompleteInput from "@/components/inputs/AutoCompleteInput";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useAuth from "@/lib/auth/useAuth";
import TextInput from "@/components/inputs/TextInput";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCheck,
    faClose,
    faDrawPolygon,
    faPlus,
    faSave,
    faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import {
    FillLayerSpecification,
    LineLayerSpecification,
    MapMouseEvent,
    SymbolLayerSpecification,
} from "mapbox-gl";
import useMap from "@/lib/map/useMap";
import {
    addUpdateGeojsonLayers,
    addUpdateGeojsonSource,
    getPointOnBoundary,
    moveLayersToTop,
} from "@/lib/utils/map";
import * as turf from "@turf/turf";
import { MultiPolygon, Point, Polygon } from "geojson";
import { Feature } from "geojson";
import useMapboxEvent from "@/lib/map/useMapboxHandler";
import { IrrigationSystem } from "@/types/irrigationSystem";
import useRequest from "@/lib/request/useRequest";
import { useTranslation } from "react-i18next";

const schema = object().shape({
    treatments: array().of(string().required()),
    sectors: array().of(
        object().shape({
            section_number: number().required(),
            treatment: string().required(),
            start_degree: number().required(),
            stop_degree: number().required(),
        }),
    ),
});

type FormType = InferType<typeof schema>;

const COLUMNS = [
    {
        key: "section_number",
        label: "Sector ",
    },
    {
        key: "treatment",
        label: "Treatment",
    },
    {
        key: "start_degree",
        label: "Start",
    },
    {
        key: "stop_degree",
        label: "Stop",
    },
];

const TREATMENT_SECTORS_SOURCE = "treatment-sectors";
const TREATMENT_SECTORS_FILL_LAYER: FillLayerSpecification = {
    id: "treatment-sectors-fill",
    source: TREATMENT_SECTORS_SOURCE,
    type: "fill",
    paint: {
        "fill-color": "#74bd43",
        "fill-opacity": 0.3,
    },
};

const TREATMENT_SECTORS_LINE_LAYER: LineLayerSpecification = {
    id: "treatment-sectors-line",
    source: TREATMENT_SECTORS_SOURCE,
    type: "line",
    paint: {
        "line-color": "#74bd43",
        "line-width": 3,
    },
};
const TREATMENT_SECTORS_TEXT_LAYER: SymbolLayerSpecification = {
    id: "treatment-sectors-text",
    source: TREATMENT_SECTORS_SOURCE,
    type: "symbol",
    layout: {
        "text-field": [
            "concat",
            ["get", "section_number"],
            "\n",
            ["get", "treatment"],
        ],
    },
};

function getSectorMultiPolygon(
    centerPoint: Point,
    irrigationSystem: IrrigationSystem,
    properties: {
        section_number: number;
        treatment: string;
        start_degree: number | string;
        stop_degree: number | string;
        irrigation_system?: number;
    },
) {
    if (
        properties.start_degree == undefined ||
        properties.start_degree == null ||
        properties.start_degree === "" ||
        properties.stop_degree == undefined ||
        properties.stop_degree == null ||
        properties.stop_degree === ""
    )
        return null;
    return turf.intersect(
        turf.featureCollection<MultiPolygon | Polygon>([
            turf.sector(
                centerPoint,
                turf.convertLength(
                    irrigationSystem.pivot_length,
                    "feet",
                    "meters",
                ),
                properties.start_degree as number,
                properties.stop_degree as number,
                {
                    steps: 1440,
                },
            ),
            turf.feature(irrigationSystem.boundary),
        ]),
        {
            properties,
        },
    );
}
function TreatmentSectors() {
    const { t } = useTranslation();
    const { useMutateRequest } = useRequest();
    const { userIsSentinel } = useAuth();
    const { fieldQuery, fieldIsAdvanced } = useFieldDashboard() ?? {};

    const irrigationSystem = useMemo(() => {
        return fieldQuery?.data?.irrigation_systems[0];
    }, [fieldQuery]);

    const [showForm, setShowForm] = useState(false);

    const [drawing, setDrawing] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    const f = useForm<FormType>({
        resolver: yupResolver(schema),
        mode: "onChange",
    });

    const [treatments] = f.watch(["treatments"]);

    const sectors = useWatch({
        control: f.control,
        name: "sectors",
    });

    const { fields, append, remove } = useFieldArray({
        control: f.control,
        name: "sectors",
    });

    const formFields = useMemo(() => {
        return (
            fields?.map((field, index) => ({
                ...field,
                index,
            })) ?? []
        );
    }, [fields]);

    useEffect(() => {
        if (
            fieldQuery?.data?.irrigation_systems[0]?.treatment_sections
                ?.features?.length
        ) {
            f.setValue("treatments", [
                ...new Set(
                    fieldQuery?.data?.irrigation_systems[0]?.treatment_sections?.features.map(
                        (feature) => feature.properties.treatment,
                    ),
                ),
            ]);
            f.setValue(
                "sectors",
                fieldQuery?.data?.irrigation_systems[0]?.treatment_sections?.features.map(
                    (feature) => ({
                        section_number: feature.properties.section_number,
                        treatment: feature.properties.treatment,
                        start_degree: feature.properties.start_degree,
                        stop_degree: feature.properties.stop_degree,
                    }),
                ) ?? [],
            );

            setShowForm(true);
        }
    }, [f, fieldQuery?.data?.irrigation_systems]);

    const treatmentOptions = useMemo(
        () =>
            treatments?.map((treatment) => ({
                id: treatment ?? "",
                description: treatment ?? "",
            })) ?? [],
        [treatments],
    );

    const [addTreatment, setAddTreatment] = useState("");

    const saveSectors = useMutateRequest({
        successMessage: t("success.sectors_saved", { ns: "messages" }),
        errorMessage: t("error.failed_to_save_sectors", { ns: "messages" }),
    });

    const { map, mapLoaded } = useMap();

    useEffect(() => {
        if (map && mapLoaded) {
            addUpdateGeojsonSource(map, TREATMENT_SECTORS_SOURCE, {
                type: "geojson",
                data: fieldQuery?.data?.irrigation_systems[0]
                    ?.treatment_sections,
            });
            addUpdateGeojsonLayers(map, [
                TREATMENT_SECTORS_FILL_LAYER,
                TREATMENT_SECTORS_LINE_LAYER,
                TREATMENT_SECTORS_TEXT_LAYER,
            ]);
            moveLayersToTop(map, [
                TREATMENT_SECTORS_FILL_LAYER.id,
                TREATMENT_SECTORS_LINE_LAYER.id,
                TREATMENT_SECTORS_TEXT_LAYER.id,
            ]);
        }
    }, [map, mapLoaded, fieldQuery?.data?.irrigation_systems]);

    const centerPoint = useMemo(() => {
        if (!irrigationSystem) return null;
        return irrigationSystem.pivot_center;
    }, [irrigationSystem]);

    const getDegreeFromMap = useCallback(
        (e: MapMouseEvent): number => {
            if (!centerPoint || !irrigationSystem) return 0;
            const point = turf.point([e.lngLat.lng, e.lngLat.lat]);
            const validatedPoint = getPointOnBoundary(
                point.geometry,
                irrigationSystem.boundary as unknown as Feature<MultiPolygon>,
            );
            const bearing = turf.bearing(centerPoint, validatedPoint);
            return Math.round(turf.bearingToAzimuth(bearing));
        },
        [centerPoint, irrigationSystem],
    );

    const onSubmit = useCallback(
        (data: FormType) => {
            if (!irrigationSystem || !centerPoint) return;

            const payload = {
                treatment_sections:
                    data?.sectors
                        ?.map((sector) => {
                            let geometry = getSectorMultiPolygon(
                                centerPoint,
                                irrigationSystem,
                                sector,
                            )?.geometry;

                            if (!geometry) return null;

                            if (geometry.type === "Polygon") {
                                geometry = {
                                    type: "MultiPolygon",
                                    coordinates: [geometry.coordinates],
                                };
                            }

                            return {
                                geometry,
                                properties: {
                                    ...sector,
                                    irrigation_system: irrigationSystem.id,
                                },
                            };
                        })
                        ?.filter((feature) => feature !== null) ?? [],
                field: irrigationSystem.field,
            };

            saveSectors.mutate({
                url: `irrigation_systems/${irrigationSystem?.id}/`,
                method: "PUT",
                data: payload,
            });
        },
        [centerPoint, irrigationSystem, saveSectors],
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const collectionLookup = useRef<any>(turf.featureCollection([]));

    useEffect(() => {
        if (!map || !mapLoaded || !centerPoint || !irrigationSystem) return;

        const newFeatures =
            sectors
                ?.map((feature) => {
                    const lookup = `${feature.section_number}-${feature.treatment}-${feature.start_degree}-${feature.stop_degree}`;
                    if (collectionLookup.current[lookup]) {
                        return collectionLookup.current[lookup];
                    }
                    return getSectorMultiPolygon(
                        centerPoint,
                        irrigationSystem,
                        feature,
                    ) as Feature<MultiPolygon>;
                })
                .filter((feature) => feature !== null) ?? [];

        collectionLookup.current = newFeatures.reduce(
            (acc, feature) => {
                acc[
                    `${feature.properties.section_number}-${feature.properties.treatment}-${feature.properties.start_degree}-${feature.properties.stop_degree}`
                ] = feature;
                return acc;
            },
            {} as Record<string, Feature<MultiPolygon>>,
        );

        addUpdateGeojsonSource(map, TREATMENT_SECTORS_SOURCE, {
            type: "geojson",
            data: turf.featureCollection(newFeatures),
        });

        moveLayersToTop(map, [
            TREATMENT_SECTORS_FILL_LAYER.id,
            TREATMENT_SECTORS_LINE_LAYER.id,
            TREATMENT_SECTORS_TEXT_LAYER.id,
        ]);
    }, [centerPoint, f, irrigationSystem, map, mapLoaded, sectors, drawing]);

    useMapboxEvent(map, "mousemove", (e) => {
        if (drawing) {
            const degree = getDegreeFromMap(e as MapMouseEvent);
            f.setValue(`sectors.${activeIndex}.stop_degree`, degree);
        }
    });

    useMapboxEvent(map, "click", (e) => {
        if (drawing) {
            const degree = getDegreeFromMap(e as MapMouseEvent);
            if (degree === 0) return;
            f.setValue(`sectors.${activeIndex}.stop_degree`, degree);
            append({
                section_number: fields.length + 1,
                treatment: "",
                start_degree: degree,
                stop_degree: degree + 1,
            });
            setActiveIndex((index) => index + 1);
        }
    });

    if (!irrigationSystem) {
        return null;
    }

    return (
        <div className="flex flex-col pb-24">
            <div>
                {t("treatment_sectors.title", { ns: "setup" })}
                <span className="italic text-gray-600 text-sm">
                    {t("treatment_sectors.advanced_only", { ns: "setup" })}
                </span>
            </div>
            {showForm && (
                <>
                    <div className="flex flex-col gap-2 mb-2">
                        <div className="flex gap-2">
                            <Input
                                name={t("treatment_sectors.add_treatment", {
                                    ns: "setup",
                                })}
                                value={addTreatment}
                                onValueChange={setAddTreatment}
                                aria-label={t(
                                    "treatment_sectors.add_treatment",
                                    {
                                        ns: "setup",
                                    },
                                )}
                                size="sm"
                            />
                            <Button
                                color="secondary"
                                onPress={() => {
                                    if (!addTreatment) return;
                                    f.setValue("treatments", [
                                        ...new Set([
                                            ...(treatments ?? []),
                                            addTreatment,
                                        ]),
                                    ]);
                                    setAddTreatment("");
                                }}
                                size="sm"
                            >
                                {t("treatment_sectors.add_treatment", {
                                    ns: "setup",
                                })}
                            </Button>
                        </div>
                        <div className=" gap-2">
                            {treatments?.map((treatment) => (
                                <Chip
                                    color="secondary"
                                    key={treatment}
                                    size="lg"
                                    className="m-1"
                                    endContent={
                                        <Button
                                            size="sm"
                                            variant="light"
                                            radius="full"
                                            isIconOnly
                                            aria-label={t(
                                                "treatment_sectors.remove_treatment",
                                                {
                                                    ns: "setup",
                                                },
                                            )}
                                            onPress={() => {
                                                f.setValue(
                                                    "treatments",
                                                    treatments?.filter(
                                                        (t) => t !== treatment,
                                                    ),
                                                );
                                                f.setValue(
                                                    "sectors",
                                                    sectors?.map((s) =>
                                                        s.treatment ===
                                                            treatment
                                                            ? {
                                                                ...s,
                                                                treatment: "",
                                                            }
                                                            : s,
                                                    ),
                                                );
                                            }}
                                        >
                                            <FontAwesomeIcon
                                                icon={faClose}
                                                color="white"
                                            />
                                        </Button>
                                    }
                                >
                                    {treatment}
                                </Chip>
                            ))}
                        </div>
                    </div>
                    <div className="mb-2">
                        <Button
                            color="primary"
                            onPress={() => {
                                if (drawing) {
                                    setDrawing(false);
                                } else {
                                    if (fields.length === 0) {
                                        append({
                                            section_number: 1,
                                            treatment: "",
                                            start_degree:
                                                irrigationSystem?.pivot_start_degree ??
                                                0,
                                            stop_degree:
                                                (irrigationSystem?.pivot_start_degree ??
                                                    0) + 1,
                                        });
                                        setActiveIndex(0);
                                    } else {
                                        setActiveIndex(fields?.length - 1);
                                    }
                                    setDrawing(true);
                                }
                            }}
                            startContent={
                                <FontAwesomeIcon
                                    icon={drawing ? faCheck : faDrawPolygon}
                                />
                            }
                        >
                            {drawing
                                ? t("treatment_sectors.finish_draw", {
                                    ns: "setup",
                                })
                                : t("treatment_sectors.draw_sectors", {
                                    ns: "setup",
                                })}
                        </Button>
                    </div>
                    <FormProvider {...f}>
                        <Table
                            aria-label="Treatment Sectors"
                            selectionMode="none"
                            classNames={{
                                td: "text-xs px-[0.25rem]",
                                th: "text-xs px-[0.25rem] font-bold",
                                tr: "text-xs px-[0.25rem]",
                                wrapper: "p-[0.5rem]",
                            }}
                        >
                            <TableHeader columns={COLUMNS}>
                                {(column) => (
                                    <TableColumn key={column.key}>
                                        {column.label}
                                    </TableColumn>
                                )}
                            </TableHeader>
                            <TableBody
                                emptyContent={"No sectors found"}
                                loadingContent={<Spinner label="Loading..." />}
                            >
                                {formFields.map((item) => {
                                    return (
                                        <TableRow
                                            key={item.id}
                                            className="hover:bg-gray-100 rounded-lg"
                                        >
                                            {(columnKey) => {
                                                if (
                                                    columnKey === "start_degree"
                                                ) {
                                                    return (
                                                        <TableCell className="text-xs">
                                                            <TextInput
                                                                aria-label={t(
                                                                    "treatment_sectors.start_degree",
                                                                    {
                                                                        ns: "setup",
                                                                    },
                                                                )}
                                                                name={`sectors.${item.index}.start_degree`}
                                                                hideErrorMessage
                                                                decimalPlaces={
                                                                    1
                                                                }
                                                                max={360}
                                                                min={0}
                                                                size="sm"
                                                                type="number"
                                                            />
                                                        </TableCell>
                                                    );
                                                }

                                                if (
                                                    columnKey === "stop_degree"
                                                ) {
                                                    return (
                                                        <TableCell className="text-xs">
                                                            <div className="flex gap-2 items-center">
                                                                <TextInput
                                                                    aria-label={t(
                                                                        "treatment_sectors.stop_degree",
                                                                        {
                                                                            ns: "setup",
                                                                        },
                                                                    )}
                                                                    name={`sectors.${item.index}.stop_degree`}
                                                                    hideErrorMessage
                                                                    decimalPlaces={
                                                                        1
                                                                    }
                                                                    max={360}
                                                                    min={0}
                                                                    size="sm"
                                                                    type="number"
                                                                />
                                                                <Button
                                                                    size="sm"
                                                                    variant="light"
                                                                    isIconOnly
                                                                    color="danger"
                                                                    aria-label={t(
                                                                        "treatment_sectors.remove_sector",
                                                                        {
                                                                            ns: "setup",
                                                                        },
                                                                    )}
                                                                    onPress={() => {
                                                                        remove(
                                                                            item.index,
                                                                        );
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={
                                                                            faTrashCan
                                                                        }
                                                                    />
                                                                </Button>
                                                            </div>
                                                        </TableCell>
                                                    );
                                                }

                                                if (columnKey === "treatment") {
                                                    return (
                                                        <TableCell className="text-xs">
                                                            <AutoCompleteInput
                                                                aria-label={t(
                                                                    "treatment_sectors.treatment",
                                                                    {
                                                                        ns: "setup",
                                                                    },
                                                                )}
                                                                name={`sectors.${item.index}.treatment`}
                                                                options={
                                                                    treatmentOptions
                                                                }
                                                                hideErrorMessage
                                                                size="sm"
                                                            />
                                                        </TableCell>
                                                    );
                                                }
                                                if (
                                                    columnKey ===
                                                    "section_number"
                                                ) {
                                                    return (
                                                        <TableCell className="text-xs">
                                                            {item.index + 1}
                                                        </TableCell>
                                                    );
                                                }
                                                return (
                                                    <TableCell className="text-xs">
                                                        {getKeyValue(
                                                            item,
                                                            columnKey,
                                                        ) ?? "--"}
                                                    </TableCell>
                                                );
                                            }}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                        <div className="flex justify-between mt-2">
                            <Button
                                color="primary"
                                onPress={() => {
                                    append({
                                        section_number: fields.length + 1,
                                        treatment: "",
                                        start_degree:
                                            fields[fields.length - 1]
                                                ?.stop_degree ?? 0,
                                        stop_degree:
                                            (fields[fields.length - 1]
                                                ?.stop_degree ?? 0) + 1,
                                    });
                                }}
                                startContent={<FontAwesomeIcon icon={faPlus} />}
                                isLoading={saveSectors.isPending}
                                isDisabled={saveSectors.isPending}
                            >
                                {t("treatment_sectors.add_row", {
                                    ns: "setup",
                                })}
                            </Button>

                            <Button
                                color="secondary"
                                startContent={<FontAwesomeIcon icon={faSave} />}
                                onPress={() => {
                                    f.trigger();
                                    f.handleSubmit(onSubmit)();
                                }}
                                isLoading={saveSectors.isPending}
                                isDisabled={saveSectors.isPending}
                            >
                                {t("save", {
                                    ns: "common",
                                })}
                            </Button>
                        </div>
                    </FormProvider>
                </>
            )}

            {!showForm && (
                <div className="flex flex-col gap-2">
                    <Button
                        isDisabled={
                            !fieldIsAdvanced &&
                            !userIsSentinel &&
                            !irrigationSystem
                        }
                        color={
                            !fieldIsAdvanced &&
                                !userIsSentinel &&
                                !irrigationSystem
                                ? "default"
                                : "primary"
                        }
                        onPress={() => {
                            append({
                                section_number: 1,
                                treatment: "",
                                start_degree:
                                    irrigationSystem?.pivot_start_degree ?? 0,
                                stop_degree:
                                    (irrigationSystem?.pivot_start_degree ??
                                        0) + 1,
                            });
                            setShowForm(true);
                        }}
                    >
                        {t("treatment_sectors.add_sectors", {
                            ns: "setup",
                        })}
                    </Button>
                </div>
            )}
        </div>
    );
}

export default TreatmentSectors;
